import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { getLocaleFromPathname, type Locale } from "@/lib/i18n";
import { getPageTranslation } from "@/lib/translations";

import produktionImg from "@/assets/company/althaus-werk2-aerial-new.jpg";
import hauptsitzImg from "@/assets/company/althaus-hauptsitz-strasse.jpg";
import teamImg from "@/assets/company/teamwork-collaboration.jpg";

interface AboutPreviewSectionProps {
  locale?: Locale;
}

export function AboutPreviewSection({ locale: propLocale }: AboutPreviewSectionProps) {
  const location = useLocation();
  const locale = propLocale || getLocaleFromPathname(location.pathname);
  const pt = getPageTranslation(locale);
  const [currentIndex, setCurrentIndex] = useState(0);

  const images = [
    { name: pt.aboutPreview.headquarters, image: hauptsitzImg },
    { name: pt.aboutPreview.production, image: produktionImg },
    { name: pt.aboutPreview.team, image: teamImg },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <section className="pt-28 md:pt-40 pb-16 md:pb-24 bg-white dark:bg-background">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl md:text-[45px] font-normal text-foreground leading-relaxed tracking-wide mb-8">
            {pt.aboutPreview.title}
          </h2>
          <div className="max-w-5xl mx-auto space-y-4">
            <p className="text-lg md:text-[22px] font-extralight text-foreground/80 leading-relaxed tracking-wide">
              {pt.aboutPreview.description}
            </p>
            <p className="text-lg md:text-[22px] font-extralight text-foreground/80 leading-relaxed tracking-wide">
              {pt.aboutPreview.description2}
            </p>
          </div>
        </motion.div>

        <div className="relative max-w-5xl mx-auto rounded-xl overflow-hidden aspect-[16/9]">
          <AnimatePresence initial={false}>
            <motion.img
              key={currentIndex}
              src={images[currentIndex].image}
              alt={images[currentIndex].name}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
